import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useGroupMutations, useGroups } from '../../hooks/useGroups';
import { ProgressBar } from '../../components/ProgressBar';
import { Modal } from '../../components/Modal';
import { FormShell, Field, TextArea, fieldErrorsFromApiError, type FieldErrors } from '../../components/FormShell';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { fmtDateTime, fmtMonth, fmtUsd } from '../../lib/format';
import type { CreateGroupRequest, SpendGroupDto } from '../../lib/types';

export function Groups() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }
  return <GroupsInner />;
}

function GroupsInner() {
  const groups = useGroups();
  const { create } = useGroupMutations();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">Spend groups</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Group developers (and, later, API keys and workspaces), set a monthly target, and members get an email when the group crosses each threshold.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="ml-auto rounded-md bg-atg-navy text-white px-4 py-2 text-sm font-medium hover:bg-atg-blue">
          New group
        </button>
      </div>

      {groups.isLoading && <div className="text-slate-500">Loading groups…</div>}
      {groups.error && <div className="text-red-600 text-sm">Failed to load groups.</div>}

      {groups.data && (
        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-2">Group</th>
                <th className="px-4 py-2 text-right">Members</th>
                <th className="px-4 py-2 text-right">{groups.data[0] ? fmtMonth(groups.data[0].month) : 'MTD'}</th>
                <th className="px-4 py-2 text-right">Target</th>
                <th className="px-4 py-2 w-64">Progress</th>
                <th className="px-4 py-2">Last alert</th>
              </tr>
            </thead>
            <tbody>
              {groups.data.map(g => <GroupRow key={g.id} g={g} />)}
              {groups.data.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No groups yet. Create one to start tracking a team against a target.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New spend group">
        <GroupForm
          submitLabel="Create"
          pending={create.isPending}
          onSubmit={async body => {
            const g = await create.mutateAsync(body);
            toast.success(`Group "${g.name}" created.`);
            setShowCreate(false);
          }}
        />
      </Modal>
    </div>
  );
}

function GroupRow({ g }: { g: SpendGroupDto }) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-4 py-3">
        <Link to={`/admin/groups/${g.id}`} className="font-medium text-atg-blue hover:underline">{g.name}</Link>
        {g.description && <div className="text-xs text-slate-500">{g.description}</div>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{g.memberCount}</td>
      <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtUsd(g.mtdUsd)}</td>
      <td className="px-4 py-3 text-right tabular-nums">{g.monthlyTargetUsd !== null ? fmtUsd(g.monthlyTargetUsd) : <span className="text-slate-400">none</span>}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <ProgressBar pct={g.pct} thresholds={g.thresholds} className="flex-1" />
          <span className="w-10 text-right text-xs tabular-nums text-slate-600">{g.pct !== null ? `${g.pct}%` : '—'}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {g.lastNotifiedPct !== null ? <span><span className="font-medium text-atg-orange">{g.lastNotifiedPct}%</span> · {fmtDateTime(g.lastNotifiedAt)}</span> : <span className="text-slate-400">none this month</span>}
      </td>
    </tr>
  );
}

/** Shared by create and edit. Thresholds are entered as comma-separated percentages. */
export function GroupForm({ initial, submitLabel, pending, onSubmit }: {
  initial?: { name: string; description: string | null; monthlyTargetUsd: number | null; thresholds: number[]; notifyAdmins: boolean };
  submitLabel: string;
  pending: boolean;
  onSubmit: (body: CreateGroupRequest) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [target, setTarget] = useState(initial?.monthlyTargetUsd?.toString() ?? '');
  const [thresholds, setThresholds] = useState((initial?.thresholds ?? [50, 75, 90]).join(', '));
  const [notifyAdmins, setNotifyAdmins] = useState(initial?.notifyAdmins ?? true);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setFormError(null);
    const parsedThresholds = thresholds.split(/[,\s]+/).filter(Boolean).map(Number);
    if (parsedThresholds.some(n => !Number.isInteger(n) || n < 1 || n > 1000)) {
      setErrors({ thresholds: ['Enter whole percentages between 1 and 1000, separated by commas.'] });
      return;
    }
    const body: CreateGroupRequest = {
      name: name.trim(),
      description: description.trim() || null,
      monthlyTargetUsd: target.trim() === '' ? null : Number(target),
      thresholds: parsedThresholds.length > 0 ? parsedThresholds : null,
      notifyAdmins
    };
    try {
      await onSubmit(body);
    } catch (err) {
      const fe = fieldErrorsFromApiError(err);
      if (Object.keys(fe).length > 0) setErrors(fe);
      else setFormError(((err as ApiError).body as { error?: string } | undefined)?.error ?? 'Could not save the group.');
    }
  }

  return (
    <FormShell onSubmit={submit} formError={formError}>
      <Field label="Name" name="name" value={name} onChange={e => setName(e.target.value)} required maxLength={128} errors={errors} />
      <TextArea label="Description" name="description" value={description} onChange={e => setDescription(e.target.value)} rows={2} maxLength={512} errors={errors} />
      <Field label="Monthly target (USD)" name="monthlyTargetUsd" type="number" min={0} step="1" value={target} onChange={e => setTarget(e.target.value)} errors={errors}
        help="Leave blank to track the group without alerts." />
      <Field label="Alert thresholds (% of target)" name="thresholds" value={thresholds} onChange={e => setThresholds(e.target.value)} errors={errors}
        help="Each fires once per month; a jump past several sends one email for the highest." />
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={notifyAdmins} onChange={e => setNotifyAdmins(e.target.checked)} />
        Copy the DevSpend admins on this group's emails
      </label>
      <div className="flex justify-end">
        <button type="submit" disabled={pending} className="rounded-md bg-atg-navy text-white px-4 py-2 text-sm font-medium hover:bg-atg-blue disabled:opacity-50">
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </FormShell>
  );
}
